import type { Workspace } from '@store/reducers/dashboard/types'
import { countWorkspaceProjects } from '@store/reducers/dashboard/utils'
import type React from 'react'

import { clsJoin } from '@/utils'
import {
  ActivityLink,
  Folders,
  MemberAvatars,
  MembersLink,
  SettingsLink,
  SubscriptionInfo,
  WorkspacesDropdown,
} from './components'
import css from './styles.module.css'

interface Props {
  onCloseSidebar: () => void
  workspaces: Workspace[]
  current: Workspace
  activeFolder: string | null
}

export const WorkspaceSidebar: React.FC<Props> = ({
  onCloseSidebar,
  workspaces,
  current,
  activeFolder,
}) => {
  const personalSpace = current.type === 'personal'
  const showFolderAccess = current.features.includes('folder_access')
  const showSettings =
    !current.expired && current.features.includes('team_settings')
  const showMembers = !current.expired

  const teamSectionCls = clsJoin(
    css.sidebar_section,
    personalSpace && css.personal
  )

  const projects = countWorkspaceProjects(current)

  const activeMembers = new Set(
    workspaces.flatMap(({ members }) =>
      members
        .filter(({ activity }) => activity === 'active')
        .map(({ id }) => id)
    )
  ).size

  return (
    <div className={css.sidebar_container}>
      <div className={css.sidebar_header}>
        <WorkspacesDropdown
          current={current}
          workspaces={workspaces}
          onCloseSidebar={onCloseSidebar}
        />
      </div>

      <div className={css.sidebar_body}>
        <div className={css.sidebar_section}>
          <Folders
            showFolderAccess={showFolderAccess}
            canCreate={!personalSpace}
            activeFolder={activeFolder}
            workspace={current}
          />
        </div>
        <div className={teamSectionCls}>
          {showSettings && <SettingsLink id={current.id} />}
          {showMembers && <MembersLink id={current.id} />}
          <MemberAvatars workspace={current} />
        </div>
        <div className={css.sidebar_section}>
          <ActivityLink workspaceId={current.id} />
        </div>
        <SubscriptionInfo
          storageUsage={current.storageUsage}
          projects={projects}
          members={personalSpace ? null : current.members.length}
          activeMembers={activeMembers}
        />
      </div>
    </div>
  )
}
